import { DeploymentCenterStackVersion } from '@pubstack/common/src/deployment-center'

/**
 * What a stack group is serving right now, as opposed to what it is queued to serve.
 *
 * The two are different questions and the page needs both. `state` says what has to happen to a group;
 * this says whether something is already happening to it — a group can be mid-rollout, splitting traffic
 * between an old version and a new one, and that is precisely when you want to think twice before
 * queueing another deploy on top of it.
 *
 * A deployment is "in progress" exactly when the live traffic is not all on one version: either a single
 * live version is serving a fraction, or two or more are splitting it. Only the second is the A/B case
 * `isAbDeployedGroup` names — the one the deploy plan counts, because deploying it swaps one arm of a
 * running experiment. Neither shape is refused an action: both are listed, ticked and acted on like any
 * other row.
 */
export type LiveRollout =
  /** Nothing deployed yet. Normal on Ready to deploy: a first version is queued and has never gone live. */
  | { kind: 'none' }
  /** One version, all of the traffic. The steady state. */
  | { kind: 'full'; version: number; percent: number }
  /** Traffic split, or one version on a fraction of it — a rollout that has not finished. */
  | { kind: 'partial'; shares: { version: number; percent: number }[] }

const percentOf = (ratio: number): number => Math.round(ratio * 100)

/**
 * Read the rollout off a group's versions.
 *
 * Only `live` ones count: a `to-deploy` version carries a ratio too, but it is the ratio it *would* be
 * deployed at, and reporting it as traffic would show a stack serving visitors it has never served.
 */
export const liveRolloutOf = (versions: readonly DeploymentCenterStackVersion[]): LiveRollout => {
  const live = versions.filter((version) => version.status === 'live')
  if (live.length === 0) {
    return { kind: 'none' }
  }

  const shares = live.map((version) => ({ version: version.version, percent: percentOf(version.ratio) })).sort((a, b) => b.version - a.version)
  const [only] = shares
  // One version on everything is the only shape that is not a rollout. Anything else — two versions
  // sharing, or one version deliberately held below 100% — is a deployment someone started and has not
  // finished, which is the thing this exists to make visible without expanding the row.
  if (shares.length === 1 && only && only.percent === 100) {
    return { kind: 'full', version: only.version, percent: 100 }
  }

  return { kind: 'partial', shares }
}

/**
 * The rollout in one short string, for a table cell sitting next to a Version column.
 *
 * `latestVersion` is what that column shows, and it is why the leading version number is usually left
 * out: "v3 50% · v2 50%" beside a cell already reading "v3" says v3 twice and makes the reader work out
 * which of the two numbers is the one being deployed. "50% with v2" reads as the sentence it is.
 *
 * The exception is the case that would otherwise mislead. `latestVersion` is the newest version of the
 * group, which on the Ready to deploy tab is routinely a **queued** one — so the version serving traffic
 * need not be the version named beside it. When they differ, the share spells its own version out rather
 * than letting the percentage be read against a version that is serving nobody.
 */
export const liveRolloutLabel = (rollout: LiveRollout, latestVersion?: number): string => {
  switch (rollout.kind) {
    case 'none':
      return 'Not deployed'
    case 'full':
      return '100%'
    case 'partial': {
      const [leading, ...rest] = rollout.shares
      if (!leading) {
        return 'Not deployed'
      }
      const share = leading.version === latestVersion ? `${leading.percent}%` : `v${leading.version} ${leading.percent}%`
      return rest.length === 0 ? share : `${share} with ${rest.map((other) => `v${other.version}`).join(', ')}`
    }
  }
}
